import { Component, OnInit, Type } from '@angular/core';
import { WizardPlugin } from '../../../../decorators/wizard-plugin';
import { IWizardPluginComponent } from '../../../../interfaces/wizard-plugin-component.interface';
import { IPluginPreviewComponent } from '../../../../interfaces/plugin-preview-component.interface';
import { ResourceMasterDataService } from '../../../../services/resource-master-data.service';
import { Pair } from '../../../../models/pair';
import { CloudProviderData } from '../../../../models/wizard-plugin-models/cloud-provider-data';
import { ColdObservable } from 'rxjs/internal/testing/ColdObservable';
import { BasePluginData } from '../../../../models/wizard-plugin-models/base-plugin-data';
import { CloudProviderPreviewComponent } from './cloud-provider-preview/cloud-provider-preview.component';
import { Log, IShortLogger } from '../../../../services/logger.service';

@WizardPlugin({
  name: "Cloud Provider",
  optional: false,
  description: "Configure Cloud Provider Settings.",
  dataModelType: CloudProviderData
})
@Component({
  selector: 'app-cloud-provider-plugin',
  templateUrl: './cloud-provider-plugin.component.html',
  styleUrl: './cloud-provider-plugin.component.css'
})
export class CloudProviderPluginComponent implements IWizardPluginComponent, OnInit {
  private readonly logger: IShortLogger<CloudProviderPluginComponent> = Log.create(CloudProviderPluginComponent);

  public pluginData: CloudProviderData = new CloudProviderData();

  public cloudOptions: Pair[] = [];
  public selectedCloudProvider: string = '';
  public regionOptions: Pair[] = [];
  public sizeOptions: Pair[] = [];
  public storageOptions: Pair[] = [];

  // Autocomplete state
  regionSearch: string = '';
  sizeSearch: string = '';
  storageSearch: string = '';
  filteredRegions: Pair[] = [];
  filteredSizes: Pair[] = [];
  filteredStorages: Pair[] = [];

  displayPair = (p: Pair | null): string => p?.name ?? '';

  filterRegions(query: string | any): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.filteredRegions = q
      ? this.regionOptions.filter(r => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q))
      : this.regionOptions.slice(0, 30);
  }

  filterSizes(query: string | any): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.filteredSizes = q
      ? this.sizeOptions.filter(s => s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q))
      : this.sizeOptions.slice(0, 30);
  }

  filterStorages(query: string | any): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.filteredStorages = q
      ? this.storageOptions.filter(s => s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q))
      : this.storageOptions.slice(0, 30);
  }

  onRegionSelected(pair: Pair): void {
    this.pluginData.region = pair.id;
    this.regionSearch = pair.name;
  }

  onSizeSelected(pair: Pair): void {
    this.pluginData.size = pair.id;
    this.sizeSearch = pair.name;
  }

  onStorageSelected(pair: Pair): void {
    this.pluginData.storage = pair.id;
    this.storageSearch = pair.name;
  }

  private syncSearchFields(): void {
    const region = this.regionOptions.find(r => r.id === this.pluginData.region);
    this.regionSearch = region?.name ?? '';
    const size = this.sizeOptions.find(s => s.id === this.pluginData.size);
    this.sizeSearch = size?.name ?? '';
    const storage = this.storageOptions.find(s => s.id === this.pluginData.storage);
    this.storageSearch = storage?.name ?? '';
  }

  constructor(private masterDataService: ResourceMasterDataService) { }

  getPluginData(): BasePluginData {
    return this.pluginData;
  }

  /**
   * Set plugin data and restore UI state
   * This method is called when navigating back to this plugin with saved data
   */
  setPluginData(data: BasePluginData): void {
    if (data) {
      this.pluginData = data as CloudProviderData;

      // Restore the selected cloud provider for UI
      this.selectedCloudProvider = this.pluginData.provider;

      // Trigger cloud provider change to load regions, sizes, and storage options
      if (this.pluginData.provider) {
        this.onCloudProviderChange();
      }
    }
  }

  validate(): boolean {
    return !!this.pluginData.provider &&
      this.cloudOptions.some(opt => opt.id === this.pluginData.provider) &&
      this.regionOptions.some(opt => opt.id === this.pluginData.region) &&
      this.sizeOptions.some(opt => opt.id === this.pluginData.size);
  }

  isComplete(): boolean {
    return this.validate();
  }

  reset(): void {
    this.pluginData = new CloudProviderData();
    this.selectedCloudProvider = '';
    this.regionOptions = [];
    this.sizeOptions = [];
    this.storageOptions = [];
  }

  ngOnInit(): void {
    this.loadCloudProviderData();
  }

  public selectCloudProvider(value: string) {
    this.selectedCloudProvider = value;
    this.pluginData.provider = value; // Sync with your config model
    this.onCloudProviderChange();      // Call your existing handler
  }

  private loadCloudProviderData(): void {
    try {
      // Get cloud provider options
      const cloudData = this.masterDataService?.get<{
        [key: string]: {
          name: string,
          versions: Pair[]
        }
      }>('cloud');

      if (cloudData) {
        // Only show Azure
        if (cloudData['azure']) {
          this.cloudOptions = [{
            id: 'azure',
            name: cloudData['azure'].name
          }];
          // Select Azure by default
          this.selectedCloudProvider = 'azure';
          this.pluginData.provider = 'azure';
          this.onCloudProviderChange();
        } else {
          this.cloudOptions = [];
        }
      } else {
        // Fallback
        this.cloudOptions = [
          { id: 'aws', name: 'AWS' },
          // { id: 'azure', name: 'Azure' },
          // { id: 'gcp', name: 'Google Cloud Platform' }
        ];
      }
    } catch (error) {
      this.logger.e('Error loading cloud provider options:', error);
      this.cloudOptions = [
        { id: 'aws', name: 'AWS' },
        // { id: 'azure', name: 'Azure' },
        // { id: 'gcp', name: 'Google Cloud Platform' }
      ];
    }
  }

  onCloudProviderChange(): void {
    try {
      if (!this.masterDataService) {
        this.logger.w('Master data service not available');
        this.regionOptions = [];
        this.sizeOptions = [];
        this.storageOptions = [];
        return;
      }

      const cloudProvider = this.pluginData.provider;

      // Region loading
      const regionKey = `cloud.${cloudProvider}.regions`;
      const regionData = this.masterDataService.get<Pair[]>(regionKey);
      this.regionOptions = Array.isArray(regionData) ? regionData : [];

      // Size loading
      const sizeKey = `cloud.${cloudProvider}.sizes`;
      const sizeData = this.masterDataService.get<Pair[]>(sizeKey);
      this.sizeOptions = Array.isArray(sizeData) ? sizeData : [];

      // Storage loading

      this.storageOptions = this.masterDataService.get<Pair[]>(`cloud.${cloudProvider}.storage`) ?? [];

      // Don't reset selected values when restoring data
      // Only reset if the current selections are not valid for the new provider
      if (this.regionOptions.length > 0 && !this.regionOptions.some(r => r.id === this.pluginData.region)) {
        this.pluginData.region = '';
      }

      if (this.sizeOptions.length > 0 && !this.sizeOptions.some(s => s.id === this.pluginData.size)) {
        this.pluginData.size = '';
      }

      if (this.storageOptions.length > 0 && !this.storageOptions.some(st => st.id === this.pluginData.storage)) {
        this.pluginData.storage = '';
      }

      // refresh autocomplete filtered lists and search display values
      this.filteredRegions = this.regionOptions.slice(0, 30);
      this.filteredSizes = this.sizeOptions.slice(0, 30);
      this.filteredStorages = this.storageOptions.slice(0, 30);
      this.syncSearchFields();

    } catch (error) {
      this.logger.e('Error in onCloudProviderChange:', error);
      this.regionOptions = [];
      this.sizeOptions = [];
      this.storageOptions = [];
    }
  }

  /**
   * Provide a preview component for the current configuration
   */
  asPreview(): Type<IPluginPreviewComponent> {
    return CloudProviderPreviewComponent;
  }
}

